import React, { useState, useEffect } from 'react';
import { getRestaurantUsers, createRestaurantUser, updateRestaurantUser, deleteRestaurantUser } from '../../services/api';
import { useNavigate } from 'react-router-dom';

function ManageRestaurantUsersScreen() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [form, setForm] = useState({ UserID: null, Username: '', FullName: '', Password: '', Role: 'manager' });
  const navigate = useNavigate();
  const restaurantId = localStorage.getItem('restaurantId'); // Obter o ID do restaurante logado

  useEffect(() => {
    if (!restaurantId) {
      setError('ID do restaurante não encontrado. Faça login novamente.');
      setLoading(false);
      return;
    }
    fetchRestaurantUsers();
  }, [restaurantId]);

  const fetchRestaurantUsers = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await getRestaurantUsers();
      // Filtrar apenas usuários com roles 'manager' ou 'admin'
      setUsers(data.filter(user => ['manager', 'admin'].includes(user.Role)));
    } catch (err) {
      console.error('Failed to fetch restaurant users:', err);
      setError('Não foi possível carregar os usuários do restaurante.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!restaurantId) {
      setError('ID do restaurante não encontrado. Faça login novamente.');
      return;
    }

    try {
      if (form.UserID) {
        // Atualizar usuário existente
        await updateRestaurantUser(form.UserID, {
          Username: form.Username,
          FullName: form.FullName,
          Role: form.Role,
          ...(form.Password && { Password: form.Password }), // Só envia a senha se for alterada
        });
      } else {
        // Criar novo usuário
        await createRestaurantUser({
          Username: form.Username,
          FullName: form.FullName,
          Password: form.Password,
          Role: form.Role,
          RestaurantID: restaurantId,
        });
      }
      setForm({ UserID: null, Username: '', FullName: '', Password: '', Role: 'manager' }); // Limpar formulário
      fetchRestaurantUsers(); // Recarregar lista
    } catch (err) {
      console.error('Failed to save restaurant user:', err);
      setError('Falha ao salvar usuário. Verifique os dados.');
    }
  };

  const handleEdit = (user) => {
    setForm({
      UserID: user.UserID,
      Username: user.Username,
      FullName: user.FullName,
      Password: '', // Não pré-preencher a senha por segurança
      Role: user.Role,
    });
  };

  const handleDelete = async (userId) => {
    setError('');
    if (window.confirm('Tem certeza que deseja excluir este usuário?')) {
      try {
        await deleteRestaurantUser(userId);
        fetchRestaurantUsers();
      } catch (err) {
        console.error('Failed to delete restaurant user:', err);
        setError('Falha ao excluir usuário.');
      }
    }
  };

  if (loading) {
    return <div style={styles.loading}>Carregando usuários...</div>;
  }

  return (
    <div style={styles.container}>
      <h2 style={styles.title}>Gerenciar Usuários Admin/Gerente</h2>
      <button onClick={() => navigate('/restaurant/dashboard')} style={styles.backButton}>
        Voltar ao Painel
      </button>

      <div style={styles.formSection}>
        <h3>{form.UserID ? 'Editar Usuário' : 'Adicionar Novo Usuário'}</h3>
        {error && <p style={styles.error}>{error}</p>}
        <form onSubmit={handleSubmit} style={styles.form}>
          <input
            type="text"
            name="Username"
            placeholder="Usuário"
            value={form.Username}
            onChange={handleChange}
            style={styles.input}
            required
          />
          <input
            type="text"
            name="FullName"
            placeholder="Nome Completo"
            value={form.FullName}
            onChange={handleChange}
            style={styles.input}
            required
          />
          <input
            type="password"
            name="Password"
            placeholder={form.UserID ? "Nova Senha (opcional)" : "Senha"}
            value={form.Password}
            onChange={handleChange}
            style={styles.input}
            required={!form.UserID}
          />
          <select
            name="Role"
            value={form.Role}
            onChange={handleChange}
            style={styles.select}
            required
          >
            <option value="manager">Gerente</option>
            <option value="admin">Administrador</option>
          </select>
          <button type="submit" style={styles.submitButton}>
            {form.UserID ? 'Salvar Alterações' : 'Adicionar Usuário'}
          </button>
          {form.UserID && (
            <button type="button" onClick={() => setForm({ UserID: null, Username: '', FullName: '', Password: '', Role: 'manager' })} style={styles.cancelButton}>
              Cancelar Edição
            </button>
          )}
        </form>
      </div>

      <div style={styles.listSection}>
        <h3>Lista de Usuários</h3>
        {users.length === 0 ? (
          <p>Nenhum usuário cadastrado.</p>
        ) : (
          <ul style={styles.list}>
            {users.map((user) => (
              <li key={user.UserID} style={styles.listItem}>
                <span>{user.FullName} ({user.Username}) - {user.Role}</span>
                <div style={styles.actions}>
                  <button onClick={() => handleEdit(user)} style={styles.editButton}>Editar</button>
                  <button onClick={() => handleDelete(user.UserID)} style={styles.deleteButton}>Excluir</button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '20px',
    backgroundColor: '#f0f2f5',
    minHeight: '100vh',
    fontFamily: 'Arial, sans-serif',
  },
  title: {
    color: '#333',
    marginBottom: '20px',
  },
  backButton: {
    backgroundColor: '#6c757d',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    padding: '10px 15px',
    cursor: 'pointer',
    marginBottom: '20px',
  },
  formSection: {
    backgroundColor: '#fff',
    borderRadius: '8px',
    boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
    padding: '20px',
    marginBottom: '20px',
    width: '100%',
    maxWidth: '600px',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
  },
  input: {
    padding: '10px',
    marginBottom: '10px',
    border: '1px solid #ddd',
    borderRadius: '4px',
    fontSize: '16px',
  },
  select: {
    padding: '10px',
    marginBottom: '10px',
    border: '1px solid #ddd',
    borderRadius: '4px',
    fontSize: '16px',
    backgroundColor: '#fff',
  },
  submitButton: {
    backgroundColor: '#28a745',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    padding: '10px 15px',
    cursor: 'pointer',
    marginTop: '10px',
  },
  cancelButton: {
    backgroundColor: '#ffc107',
    color: '#333',
    border: 'none',
    borderRadius: '4px',
    padding: '10px 15px',
    cursor: 'pointer',
    marginTop: '10px',
  },
  listSection: {
    backgroundColor: '#fff',
    borderRadius: '8px',
    boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
    padding: '20px',
    width: '100%',
    maxWidth: '600px',
  },
  list: {
    listStyle: 'none',
    padding: 0,
  },
  listItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '10px 0',
    borderBottom: '1px solid #eee',
  },
  actions: {
    display: 'flex',
    gap: '10px',
  },
  editButton: {
    backgroundColor: '#007bff',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    padding: '8px 12px',
    cursor: 'pointer',
  },
  deleteButton: {
    backgroundColor: '#dc3545',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    padding: '8px 12px',
    cursor: 'pointer',
  },
  loading: {
    fontSize: '18px',
    color: '#555',
    marginTop: '50px',
  },
  error: {
    color: 'red',
    marginBottom: '10px',
    textAlign: 'center',
  },
};

export default ManageRestaurantUsersScreen;
